import { FC, useCallback, useEffect, useRef, useState } from "react";
import { App, Button, Flex, Input, Select } from "antd";
import { PlusOutlined } from "@ant-design/icons";
import { ColumnsType } from "antd/lib/table";
import { AnyObject } from "antd/lib/_util/type";
import { useTranslation } from "react-i18next";

import errorCodes from "@/apis/error";
import shipAddressApi from "@/apis/shipAddress";
import useDataAdd from "@/hooks/useDataAdd";
import useDataDel from "@/hooks/useDataDel";
import useDataList from "@/hooks/useDataList";
import useDataUpdate from "@/hooks/useDataUpdate";
import useFrontDelTip from "@/hooks/useFrontDelTip";
import STableLayout, {
  DEFAULT_PAGE_SIZE,
  TableLayoutRef,
} from "@/styles/STableLayout";
import { CommonListParams } from "@/types/common";
import { Customer } from "@/types/sales";
import {
  ShipAddress,
  ShipAddressDefault,
  ShipAddressType,
  ShipAddressParams,
  ShipAddressTypeOption,
} from "@/types/shipAddress";
import useShipAddressColumn from "../hooks/useShipAddressColumn";
import ShipAddressForm from "./ShipAddressForm";
import ListFilterBar from "@/components/ListFilterBar";
import useCommittedFilters from "@/components/ListFilterBar/hooks/useCommittedFilters";

const markDefaultShipAddress = (
  list: Array<ShipAddress>,
  id: string,
): Array<ShipAddress> => {
  if (!list.some(item => item.id === id)) return list;

  return list.map(item => ({
    ...item,
    default: item.id === id ? ShipAddressDefault.Yes : ShipAddressDefault.No,
  }));
};

interface ShipAddressBookProps {
  customer: Customer;
}

interface ShipAddressFilters {
  keyword: string;
  type: ShipAddressType | undefined;
}

const INITIAL_SHIP_ADDRESS_FILTERS: ShipAddressFilters = {
  keyword: "",
  type: undefined,
};

const ShipAddressBook: FC<ShipAddressBookProps> = ({ customer }) => {
  const { t } = useTranslation();
  const { notification } = App.useApp();
  const tableRef = useRef<TableLayoutRef | null>(null);
  const [list, total, listLoading, {
    fetchList,
    addItem,
    updateItem,
    rmItem,
    setList,
  }] = useDataList<
    ShipAddress,
    [string, ShipAddressType | undefined, CommonListParams<string>?]
  >({ fetchApi: shipAddressApi.list });
  const { add, loading: addLoading } = useDataAdd<
    [string, ShipAddressParams],
    ShipAddress
  >({ api: shipAddressApi.add });
  const { update, loading: updateLoading } = useDataUpdate<
    [string, ShipAddressParams],
    ShipAddress
  >({ api: shipAddressApi.update });
  const { del } = useDataDel({ api: shipAddressApi.del });

  const [typeOptions, setTypeOptions] = useState<Array<ShipAddressTypeOption>>([]);
  const {
    draftFilters,
    appliedFilters,
    updateDraftFilters,
    commitFilters,
    replaceFilters,
  } = useCommittedFilters<ShipAddressFilters>(INITIAL_SHIP_ADDRESS_FILTERS);
  const { keyword, type } = draftFilters;
  const [addOpen, setAddOpen] = useState(false);
  const [detail, setDetail] = useState<ShipAddress | null>(null);
  const [settingDefaultId, setSettingDefaultId] = useState<string | null>(null);
  const fetchListRef = useRef(fetchList);
  const notificationRef = useRef(notification);
  const translationRef = useRef(t);

  fetchListRef.current = fetchList;
  notificationRef.current = notification;
  translationRef.current = t;

  const load = useCallback((
    offset: number,
    filters: ShipAddressFilters,
  ) => fetchListRef.current(customer.id, filters.type, {
    body: filters.keyword,
    pagination: {
      offset,
      take: tableRef.current?.pageSize ?? DEFAULT_PAGE_SIZE,
    },
  })
    .catch(() => {
      notificationRef.current.error({
        placement: "top",
        message: translationRef.current("common.searchError"),
      });
    }), [customer.id]);

  useEffect(() => {
    let active = true;

    shipAddressApi.types()
      .then(options => {
        if (active) setTypeOptions(options);
      })
      .catch(() => {
        notificationRef.current.error({
          placement: "top",
          message: translationRef.current("common.searchError"),
        });
      });
    const nextFilters = replaceFilters(INITIAL_SHIP_ADDRESS_FILTERS);
    load(0, nextFilters);
    tableRef.current?.resetTablePage();

    return () => {
      active = false;
    };
  }, [load, replaceFilters]);

  const handleAdd = (data: ShipAddressParams) =>
    add(customer.id, data)
      .then(res => {
        addItem(res);
        if (res.default === 1) {
          setList(prev => markDefaultShipAddress(prev, res.id));
        }
        setAddOpen(false);
        notification.success({
          placement: "top",
          message: t("common.addSuccess"),
        });
      })
      .catch(error => {
        notification.error({
          placement: "top",
          message: t("common.addError"),
          description: errorCodes[Number(error)] || t("common.serverError"),
        });
        return Promise.reject(error);
      });

  const submitUpdate = (id: string, data: ShipAddressParams) =>
    update(id, data)
      .then(res => {
        updateItem(res);
        if (res.default === 1) {
          setList(prev => markDefaultShipAddress(prev, res.id));
        }
        notification.success({
          placement: "top",
          message: t("common.editSuccess"),
        });
        return res;
      })
      .catch(error => {
        notification.error({
          placement: "top",
          message: t("common.editError"),
          description: errorCodes[Number(error)] || t("common.serverError"),
        });
        return Promise.reject(error);
      });

  const handleUpdate = (data: ShipAddressParams) => {
    if (!detail) return Promise.reject();

    return submitUpdate(detail.id, data).then(() => setDetail(null));
  };

  const handleDelete = (id: string) =>
    del(id)
      .then(() => {
        rmItem(id);
        notification.success({
          placement: "top",
          message: t("common.rmSuccess"),
        });
      })
      .catch(error => {
        notification.error({
          placement: "top",
          message: t("common.rmError"),
        });
        return Promise.reject(error);
      });

  const { onConfirm } = useFrontDelTip({ cb: handleDelete });

  const handleSetDefault = (id: string) => {
    setSettingDefaultId(id);
    return shipAddressApi.setDefault(id)
      .then(() => {
        setList(prev => markDefaultShipAddress(prev, id));
        notification.success({
          placement: "top",
          message: t("common.editSuccess"),
        });
      })
      .catch(error => {
        notification.error({
          placement: "top",
          message: t("common.editError"),
          description: errorCodes[Number(error)] || t("common.serverError"),
        });
      })
      .finally(() => setSettingDefaultId(null));
  };

  const columns = useShipAddressColumn({
    typeOptions,
    settingDefaultId,
    onDetail: setDetail,
    onDel: onConfirm,
    onSetDefault: handleSetDefault,
  });

  const handleSearch = () => {
    const nextFilters = commitFilters();
    tableRef.current?.resetTablePage();
    load(0, nextFilters);
  };

  const handleClearFilters = () => {
    const nextFilters = replaceFilters(INITIAL_SHIP_ADDRESS_FILTERS);
    tableRef.current?.resetTablePage();
    load(0, nextFilters);
  };

  const headerChildren = (
    <Flex justify="space-between" gap={12} style={{ width: "100%" }}>
      <Flex gap={8}>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => setAddOpen(true)}
        >
          {t("sale.addShipAddress")}
        </Button>
      </Flex>
      <div style={{ flex: 1, minWidth: 0 }}>
        <ListFilterBar
          onSearch={handleSearch}
          onReset={handleClearFilters}
          disabled={listLoading}
        >
          {({ submit }) => (
            <>
              <Select<ShipAddressType>
                allowClear
                value={type}
                placeholder={t("sale.shipAddressType")}
                style={{ width: 180 }}
                options={typeOptions.map(item => ({
                  value: item.key,
                  label: item.desc,
                }))}
                onChange={nextType => updateDraftFilters({ type: nextType })}
              />
              <Input
                value={keyword}
                placeholder={t("sale.bySearchShipAddress")}
                style={{ width: 240 }}
                onChange={event =>
                  updateDraftFilters({ keyword: event.target.value })
                }
                onPressEnter={submit}
              />
            </>
          )}
        </ListFilterBar>
      </div>
    </Flex>
  );

  return (
    <Flex vertical style={{ width: "100%", height: "100%" }}>
      <STableLayout
        loading={listLoading}
        dataSource={list}
        columns={columns as ColumnsType<AnyObject>}
        scrollX={1200}
        headerChildren={headerChildren}
        tableLayoutRef={tableRef}
        total={total}
        handleChange={offset => load(offset, appliedFilters)}
      />
      {addOpen && (
        <ShipAddressForm
          key="add-ship-address"
          open
          data={null}
          typeOptions={typeOptions}
          confirmLoading={addLoading}
          leftTitle={t("sale.addShipAddress")}
          handleCancel={() => setAddOpen(false)}
          handleOk={handleAdd}
        />
      )}
      {detail && (
        <ShipAddressForm
          key={detail.id}
          open
          data={detail}
          typeOptions={typeOptions}
          confirmLoading={updateLoading}
          leftTitle={t("common.edit")}
          handleCancel={() => setDetail(null)}
          handleOk={handleUpdate}
        />
      )}
    </Flex>
  );
};

export default ShipAddressBook;
